/**
 * The AI usage page state: the period (a month or a whole year), the tab in
 * `?tab=`, and the filters and list views Breakdown and Runs share.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Period, RunGroupBy } from '@/api/types';
import { currentMonth } from '@/ui/format';

import { DEFAULT_BREAKDOWN, type BreakdownView } from './BreakdownTab';
import { NO_FILTERS, WHOLE_MONTH, type RunFilters } from './RunFilterBar';
import { DEFAULT_RUNS, type RunsView } from './RunsTab';
import { isYear } from './usageUi';

export const TAB_KEYS = ['overview', 'breakdown', 'runs'] as const;
export type TabKey = (typeof TAB_KEYS)[number];

function tabOf(param: string | null): TabKey {
  return TAB_KEYS.find((t) => t === param) ?? 'overview';
}

/**
 * The month to show when switching a year back to Month: the month last shown
 * when it is in this year, else this year's latest month.
 */
export function monthOfYear(year: Period, lastMonth: string): Period {
  if (lastMonth.startsWith(year)) return lastMonth;
  return currentMonth().startsWith(year) ? currentMonth() : `${year}-12`;
}

export function useUsagePage() {
  const [period, setPeriod] = useState<Period>(currentMonth);
  // The month to come back to when switching from Year to Month.
  const [lastMonth, setLastMonth] = useState(currentMonth);
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params.get('tab'));
  const [filters, setFilters] = useState<RunFilters>(NO_FILTERS);
  const [runsView, setRunsView] = useState<RunsView>(DEFAULT_RUNS);
  const [breakdown, setBreakdown] = useState<BreakdownView>(DEFAULT_BREAKDOWN);

  const openTab = (key: TabKey) => setParams({ tab: key }, { replace: true });

  // Any change to what matches starts both lists again at page 1.
  function changeFilters(patch: Partial<RunFilters>) {
    setFilters((f) => ({ ...f, ...patch }));
    setRunsView((v) => ({ ...v, page: 1 }));
    setBreakdown((b) => ({ ...b, page: 1 }));
  }

  // Another period is another list; the When filter's days belonged to the
  // old one, so it goes back to the whole period.
  function changePeriod(next: Period) {
    setPeriod(next);
    if (!isYear(next)) {
      setLastMonth(next);
      // Only a year breaks down by month.
      setBreakdown((b) => (b.by === 'month' ? { ...b, by: 'day', page: 1 } : b));
    }
    changeFilters({ when: WHOLE_MONTH, whenUtc: false });
  }

  function switchKind(toYear: boolean) {
    if (toYear === isYear(period)) return;
    changePeriod(toYear ? period.slice(0, 4) : monthOfYear(period, lastMonth));
  }

  // From Overview (whole-month figures): start from no filters, so the
  // numbers match the panel that was clicked.
  function fromOverview(patch: Partial<RunFilters>, to: TabKey, by?: RunGroupBy) {
    changeFilters({ ...NO_FILTERS, ...patch });
    if (by) setBreakdown((b) => ({ ...b, by }));
    openTab(to);
  }

  const changeRunsView = (patch: Partial<RunsView>) => setRunsView((v) => ({ ...v, ...patch }));
  const changeBreakdown = (patch: Partial<BreakdownView>) => setBreakdown((b) => ({ ...b, ...patch }));

  return {
    period,
    tab,
    filters,
    runsView,
    breakdown,
    openTab,
    changeFilters,
    changePeriod,
    switchKind,
    fromOverview,
    changeRunsView,
    changeBreakdown,
  };
}

export type UsagePageState = ReturnType<typeof useUsagePage>;
